"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { useThemeConfig } from "@/hooks/use-theme-config"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = ({ className, ref, ...props }: React.ComponentPropsWithRef<typeof DialogPrimitive.Overlay>) => {
  const { isTerminal, mounted } = useThemeConfig()

  // Hydration-safe pattern: only show terminal styling after mount
  const showTerminal = mounted && isTerminal

  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-50",
        showTerminal
          ? "bg-emerald-950/80"
          : "bg-black/80",
        className
      )}
      {...props}
    />
  )
}
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

type DialogContentProps = React.ComponentPropsWithRef<typeof DialogPrimitive.Content> & { hideClose?: boolean; overlayClassName?: string }

const DialogContent = ({ className, children, hideClose, overlayClassName, ref, ...props }: DialogContentProps) => {
  const { isTerminal, hasGlow, mounted } = useThemeConfig()

  // Hydration-safe pattern: only show terminal styling after mount
  const showTerminal = mounted && isTerminal
  const showGlow = mounted && hasGlow

  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border p-6 shadow-lg sm:rounded-lg",
          showTerminal
            ? cn(
                "bg-black border-emerald-500/50",
                showGlow && "shadow-[0_0_30px_rgba(16,185,129,0.3)]"
              )
            : "bg-background",
          className
        )}
        {...props}
      >
        {children}
        {!hideClose && (
          <DialogPrimitive.Close
            className={cn(
              "absolute right-2 top-2 grid h-10 w-10 place-items-center rounded-sm opacity-70 ring-offset-background hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground",
              showTerminal && "text-emerald-400 hover:text-emerald-300 focus:ring-emerald-500"
            )}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const { isTerminal, mounted } = useThemeConfig()

  // Hydration-safe pattern
  const showTerminal = mounted && isTerminal

  return (
    <div
      className={cn(
        "flex flex-col space-y-1.5 text-center sm:text-left",
        showTerminal && "text-emerald-400",
        className
      )}
      {...props}
    />
  )
}
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = ({ className, ref, ...props }: React.ComponentPropsWithRef<typeof DialogPrimitive.Title>) => {
  const { isTerminal, mounted } = useThemeConfig()

  // Hydration-safe pattern
  const showTerminal = mounted && isTerminal

  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(
        "text-lg font-semibold leading-none tracking-tight",
        showTerminal && "text-emerald-400 font-mono",
        className
      )}
      {...props}
    />
  )
}
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = ({ className, ref, ...props }: React.ComponentPropsWithRef<typeof DialogPrimitive.Description>) => {
  const { isTerminal, mounted } = useThemeConfig()

  // Hydration-safe pattern
  const showTerminal = mounted && isTerminal

  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn(
        "text-sm",
        showTerminal ? "text-emerald-500/70" : "text-muted-foreground",
        className
      )}
      {...props}
    />
  )
}
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
